import { Icon, Popover } from '@cloudscape-design/components';
import type { IconProps } from '@cloudscape-design/components';
import { Box } from '@cloudscape-design/components';
import type { StatisticStatus } from '../common/types';
import type { RatingTrend } from '../common/scorecardInsights';
import { RatingTrendIndicator } from './RatingTrendIndicator';

export type StatisticBoxProps = {
  statistic: number | string;
  percentile: number;
  status: StatisticStatus;
  details?: React.ReactNode;
  icon: IconProps.Name;
  label: string;
  trend?: RatingTrend | null;
  compact?: boolean;
};

const STATUS_STYLES: Record<
  StatisticStatus,
  { footer: string; icon: IconProps.Name; label: string }
> = {
  'above-average': {
    footer: 'bg-emerald-700 text-white',
    icon: 'status-positive',
    label: 'Above Average',
  },
  average: {
    footer: 'bg-blue-700 text-white',
    icon: 'status-info',
    label: 'Average',
  },
  'below-average': {
    footer: 'bg-amber-300 text-black',
    icon: 'status-warning',
    label: 'Below Average',
  },
  'extremely-low': {
    footer: 'bg-red-700 text-white',
    icon: 'status-negative',
    label: 'Extremely Low',
  },
};

export function StatisticBox(props: StatisticBoxProps) {
  const { statistic, percentile, status, icon, label, details, trend, compact = false } = props;
  const styles = STATUS_STYLES[status];

  const indicatorContent = (
    <span
      data-testid="statistic-status"
      className="inline-flex items-center justify-center gap-2 text-inherit"
    >
      <Icon name={styles.icon} size="inherit" />
      <span>{styles.label}</span>
    </span>
  );

  return (
    <article
      className={`flex flex-col overflow-hidden rounded-xl border border-slate-300 bg-white text-slate-950 ${compact ? 'h-32' : 'h-48'}`}
      data-status={status}
    >
      <div
        className={`flex flex-1 flex-col items-center justify-center px-3 text-center ${compact ? 'min-h-20 py-2' : 'min-h-32 py-4'}`}
      >
        <div
          className={`flex items-center justify-center gap-2 font-semibold ${compact ? 'text-sm' : 'text-base'}`}
        >
          <Icon name={icon} size="inherit" />
          <span>{label}</span>
        </div>
        <div
          className={`font-extrabold leading-none tracking-tight ${compact ? 'mt-2 text-2xl' : 'mt-4 text-3xl'}`}
        >
          {statistic}
        </div>
        {details ? (
          <div className={`${compact ? 'mt-1 text-xs' : 'mt-2 text-base'}`}>{details}</div>
        ) : null}
        {trend ? <RatingTrendIndicator trend={trend} /> : null}
      </div>
      <div
        data-testid="statistic-footer"
        className={`flex items-center justify-center px-3 text-center font-semibold ${compact ? 'min-h-9 py-1 text-xs' : 'min-h-12 py-2 text-base'} ${styles.footer}`}
      >
        <Popover
          position="bottom"
          size="small"
          triggerType="text"
          header={label}
          content={
            <Box variant="p" textAlign="center">
              Better than {(100 * percentile).toFixed(1)}% of similar businesses
            </Box>
          }
        >
          {indicatorContent}
        </Popover>
      </div>
    </article>
  );
}
